import { FileBarChart, Users2, BarChart3, Activity, ArrowRight, Search, ShieldCheck, Layers } from "lucide-react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useTheme } from "../../../shared/common/ThemeContext.jsx";
import { IS_DEV } from "../App.jsx";

const SHOW_EXTRA_REPORTS = false;

function Tag({ children, tone = "neutral" }) {
  const toneMap = {
    neutral: "border-gray-300 text-gray-700",
    sky: "border-sky-300 text-sky-700",
    amber: "border-amber-300 text-amber-700",
    emerald: "border-emerald-300 text-emerald-700",
    violet: "border-violet-300 text-violet-700",
    indigo: "border-indigo-300 text-indigo-700",
  };
  return (
    <span className={`inline-flex items-center rounded-md border px-2 py-0.5 text-xs ${toneMap[tone]}`}>
      {children}
    </span>
  );
}

function DTagDark({ children, tone = "neutral" }) {
  const toneMap = {
    neutral: "border-gray-700 text-gray-200",
    sky: "border-sky-700 text-sky-300",
    amber: "border-amber-700 text-amber-300",
    emerald: "border-emerald-700 text-emerald-300",
    violet: "border-violet-700 text-violet-300",
    indigo: "border-indigo-700 text-indigo-300",
  };
  return (
    <span className={`inline-flex items-center rounded-md border px-2 py-0.5 text-xs ${toneMap[tone]}`}>
      {children}
    </span>
  );
}

export default function ReportsHub() {
  const { t } = useTranslation();
  const { isDarkMode } = useTheme();

  const shell = isDarkMode
    ? "bg-gray-900 text-gray-100"
    : "bg-gray-100 text-gray-900";

  const gridTitle = isDarkMode ? "text-gray-300" : "text-gray-600";

  const cardBase = `
    group relative block overflow-hidden rounded-2xl border p-5 transition-all
    outline-none
    hover:-translate-y-0.5 hover:shadow-xl focus-within:-translate-y-0.5 focus-within:shadow-xl
  `;
  const cardTone = isDarkMode
    ? "bg-gray-800 border-gray-700"
    : "bg-white border-gray-200";

  const iconWrap = (bg, color) =>
    `flex h-11 w-11 items-center justify-center rounded-xl ${bg} ${color}`;

  const Divider = () => (
    <div className={isDarkMode ? "h-px bg-gray-700 my-4" : "h-px bg-gray-200 my-4"} />
  );

  const TagComp = isDarkMode ? DTagDark : Tag;

  return (
    <div className={`${shell} min-h-screen p-6`}>
      <div className="mx-auto max-w-6xl">
        {/* Header */}
        <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
          <div>
              <h1 className="text-3xl font-bold">{t("pages.reports.title")}</h1>
            <p className={`${gridTitle} mt-1`}>
              {t("pages.reports.subtitle")}
            </p>
          </div>

          {/* Faux champ de recherche (future évolution) */}
          <div
            className={`inline-flex items-center gap-2 rounded-xl border px-3 py-2 text-sm ${
              isDarkMode ? "border-gray-700 bg-gray-800" : "border-gray-300 bg-white"
            }`}
            aria-label={t("pages.reports.search_aria")}
            title={t("pages.reports.search_soon")}
          >
            <Search className={isDarkMode ? "text-gray-400" : "text-gray-500"} size={16} />
            <span className={isDarkMode ? "text-gray-400" : "text-gray-500"}>{t("pages.reports.search_soon")}</span>
          </div>
        </div>

        {/* Grid */}
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          {/* Club (Team) Report */}
          <div className="relative min-w-0">
          <Link
            to="/reports/club"
            className={`${cardBase} ${cardTone}`}
            aria-label={t("pages.reports.club.open_aria")}
          >
            {/* Accent ribbon */}
            <div className="pointer-events-none absolute inset-x-0 -top-20 h-40 bg-gradient-to-b from-amber-500/10 to-transparent" />
            <div className="relative flex items-start gap-4">
              <span className={iconWrap("bg-amber-500/10", "text-amber-500")}>
                <FileBarChart />
              </span>
              <div className="flex-1">
                <div className="mb-1 flex items-center gap-2">
                  <h3 className="text-lg font-semibold">{t("pages.reports.club.title")}</h3>
                  <TagComp tone="neutral">{t("pages.reports.club.tag_overview")}</TagComp>
                  <TagComp tone="amber">{t("pages.reports.club.tag_kpis")}</TagComp>
                </div>
                <p className={`${gridTitle} text-sm`}>
                  {t("pages.reports.club.description")}
                </p>

                <Divider />

                {/* What’s inside */}
                <ul className={`${gridTitle} mb-4 space-y-1 text-sm`}>
                  <li className="flex items-center gap-2">
                    <BarChart3 size={14} /> {t("pages.reports.club.bullet_identity")}
                  </li>
                  <li className="flex items-center gap-2">
                    <Activity size={14} /> {t("pages.reports.club.bullet_load")}
                  </li>
                  <li className="flex items-center gap-2">
                    <ShieldCheck size={14} /> {t("pages.reports.club.bullet_risk")}
                  </li>
                </ul>

                <div className="flex items-center justify-between">
                  <div className="flex flex-wrap items-center gap-2">
                    <TagComp tone="violet">{t("pages.reports.printable")}</TagComp>
                  </div>
                  <span className={`inline-flex items-center text-sm font-medium ${isDarkMode ? "text-amber-300" : "text-amber-600"}`}>
                    {t("pages.reports.open")} <ArrowRight className="ml-1 transition-transform group-hover:translate-x-0.5" size={16} />
                  </span>
                </div>
              </div>
            </div>
          </Link>
          </div>

          <div className="relative min-w-0">
          <Link
            to="/reports/weekly-recap"
            className={`${cardBase} ${cardTone}`}
            aria-label={t("weekly_recap.hub.open_aria")}
          >
              <div className="pointer-events-none absolute inset-x-0 -top-20 h-40 bg-gradient-to-b from-cyan-500/10 to-transparent" />
              <div className="relative flex items-start gap-4">
                <span className={iconWrap("bg-cyan-500/10", "text-cyan-500")}>
                  <BarChart3 />
                </span>
                <div className="flex-1">
                  <div className="mb-1 flex items-center gap-2">
                    <h3 className="text-lg font-semibold">
                      {t("weekly_recap.hub.title")}
                    </h3>
                    <TagComp tone="sky">{t("weekly_recap.hub.tag_planning")}</TagComp>
                    <TagComp tone="emerald">{t("weekly_recap.hub.tag_actual")}</TagComp>
                  </div>
                  <p className={`${gridTitle} text-sm`}>
                    {t("weekly_recap.hub.description")}
                  </p>

                  <Divider />

                  <ul className={`${gridTitle} mb-4 space-y-1 text-sm`}>
                    <li className="flex items-center gap-2">
                      <Users2 size={14} /> {t("weekly_recap.hub.bullet_selector")}
                    </li>
                    <li className="flex items-center gap-2">
                      <BarChart3 size={14} /> {t("weekly_recap.hub.bullet_compare")}
                    </li>
                    <li className="flex items-center gap-2">
                      <Activity size={14} /> {t("weekly_recap.hub.bullet_global")}
                    </li>
                  </ul>

                  <div className="flex items-center justify-between">
                    <div className="flex flex-wrap items-center gap-2">
                      <TagComp tone="indigo">{t("weekly_recap.hub.tag_new")}</TagComp>
                    </div>
                    <span className={`inline-flex items-center text-sm font-medium ${isDarkMode ? "text-cyan-300" : "text-cyan-700"}`}>
                      {t("weekly_recap.hub.open")} <ArrowRight className="ml-1 transition-transform group-hover:translate-x-0.5" size={16} />
                    </span>
                  </div>
                </div>
              </div>
            </Link>
          </div>

          {SHOW_EXTRA_REPORTS && (
            <>
              {IS_DEV ? (
            // 💻 En local/dev : carte cliquable
            <Link
              to="/reports/players"
              className={`${cardBase} ${cardTone}`}
              aria-label="Open Players / Positions report"
            >
              <div className="pointer-events-none absolute inset-x-0 -top-20 h-40 bg-gradient-to-b from-sky-500/10 to-transparent" />
              <div className="relative flex items-start gap-4">
                <span className={iconWrap("bg-sky-500/10", "text-sky-500")}>
                  <Users2 />
                </span>
                <div className="flex-1">
                  <div className="mb-1 flex items-center gap-2">
                    <h3 className="text-lg font-semibold">Players / Positions report</h3>
                    <TagComp tone="sky">Comparison</TagComp>
                    <TagComp tone="neutral">Ranks</TagComp>
                    <TagComp tone="violet">Dev only</TagComp>
                  </div>
                  <p className={`${gridTitle} text-sm`}>
                    Compare players within positions: distributions, z-scores, and ranking views.
                  </p>

                  <Divider />

                  <ul className={`${gridTitle} mb-4 space-y-1 text-sm`}>
                    <li className="flex items-center gap-2">
                      <BarChart3 size={14} /> Positional averages & spread
                    </li>
                    <li className="flex items-center gap-2">
                      <Activity size={14} /> Box/Beeswarm/Heatmap views
                    </li>
                    <li className="flex items-center gap-2">
                      <ShieldCheck size={14} /> Clear outlier highlighting
                    </li>
                  </ul>

                  <div className="flex items-center justify-between">
                    <div className="flex flex-wrap items-center gap-2">
                      <TagComp tone="violet">Printable</TagComp>
                    </div>
                    <span
                      className={`inline-flex items-center text-sm font-medium ${
                        isDarkMode ? "text-sky-300" : "text-sky-600"
                      }`}
                    >
                      Open report{" "}
                      <ArrowRight
                        className="ml-1 transition-transform group-hover:translate-x-0.5"
                        size={16}
                      />
                    </span>
                  </div>
                </div>
              </div>
            </Link>
              ) : (
            // 🌐 En prod : carte "Soon", non cliquable
            <div
              className={`${cardBase} ${cardTone} opacity-60 cursor-not-allowed`}
              aria-label="Players / Positions report (coming soon)"
              title="Available soon — currently only enabled on local environments"
            >
              <div className="pointer-events-none absolute inset-x-0 -top-20 h-40 bg-gradient-to-b from-sky-500/10 to-transparent" />
              <div className="relative flex items-start gap-4">
                <span className={iconWrap("bg-sky-500/10", "text-sky-500")}>
                  <Users2 />
                </span>
                <div className="flex-1">
                  <div className="mb-1 flex items-center gap-2">
                    <h3 className="text-lg font-semibold">Players / Positions report</h3>
                    <TagComp tone="neutral">Soon</TagComp>
                  </div>
                  <p className={`${gridTitle} text-sm`}>
                    Compare players within positions: distributions, z-scores, and ranking views.
                  </p>

                  <Divider />

                  <ul className={`${gridTitle} mb-4 space-y-1 text-sm`}>
                    <li className="flex items-center gap-2">
                      <BarChart3 size={14} /> Positional averages & spread
                    </li>
                    <li className="flex items-center gap-2">
                      <Activity size={14} /> Box/Beeswarm/Heatmap views
                    </li>
                    <li className="flex items-center gap-2">
                      <ShieldCheck size={14} /> Clear outlier highlighting
                    </li>
                  </ul>

                  <div className="flex items-center justify-between">
                    <div className="flex flex-wrap items-center gap-2">
                      <TagComp tone="violet">Printable</TagComp>
                    </div>
                    <span
                      className={`inline-flex items-center text-sm font-medium ${
                        isDarkMode ? "text-gray-400" : "text-gray-500"
                      }`}
                    >
                      Coming soon
                    </span>
                  </div>
                </div>
              </div>
            </div>
              )}

              {IS_DEV && (
                <>
              {/* Collective Report */}
              <Link
                to="/reports/collectif"
                className={`${cardBase} ${cardTone}`}
                aria-label="Open Collective report"
              >
            <div className="pointer-events-none absolute inset-x-0 -top-20 h-40 bg-gradient-to-b from-emerald-500/10 to-transparent" />
            <div className="relative flex items-start gap-4">
              <span className={iconWrap("bg-emerald-500/10", "text-emerald-500")}>
                <FileBarChart />
              </span>
              <div className="flex-1">
                <div className="mb-1 flex items-center gap-2">
                  <h3 className="text-lg font-semibold">Collective report</h3>
                  <TagComp tone="emerald">Availability</TagComp>
                  <TagComp tone="neutral">Loads</TagComp>
                </div>
                <p className={`${gridTitle} text-sm`}>
                  Team view: availability, RII/MLI/fatigue windows, by-position means, daily feedback, injuries.
                </p>

                <Divider />

                <ul className={`${gridTitle} mb-4 space-y-1 text-sm`}>
                  <li className="flex items-center gap-2">
                    <Users2 size={14} /> Present / Absent breakdown
                  </li>
                  <li className="flex items-center gap-2">
                    <Activity size={14} /> Windowed RII · MLI · Fatigue
                  </li>
                  <li className="flex items-center gap-2">
                    <ShieldCheck size={14} /> Active injuries snapshot
                  </li>
                </ul>

                <div className="flex items-center justify-between">
                  <div className="flex flex-wrap items-center gap-2">
                    <TagComp tone="violet">Printable</TagComp>
                  </div>
                  <span className={`inline-flex items-center text-sm font-medium ${isDarkMode ? "text-emerald-300" : "text-emerald-600"}`}>
                    Open report <ArrowRight className="ml-1 transition-transform group-hover:translate-x-0.5" size={16} />
                  </span>
                </div>
              </div>
            </div>
              </Link>

              <Link
                to="/reports/multi-season"
                className={`${cardBase} ${cardTone}`}
                aria-label="Open Multi-season report"
              >
            <div className="pointer-events-none absolute inset-x-0 -top-20 h-40 bg-gradient-to-b from-indigo-500/10 to-transparent" />
            <div className="relative flex items-start gap-4">
              <span className={iconWrap("bg-indigo-500/10", "text-indigo-500")}>
                <Layers />
              </span>

              <div className="flex-1">
                <div className="mb-1 flex items-center gap-2">
                  <h3 className="text-lg font-semibold">Multi-season report</h3>
                  <TagComp tone="indigo">Seasons</TagComp>
                  <TagComp tone="emerald">Availability</TagComp>
                  <TagComp tone="neutral">Injuries</TagComp>
                </div>

                <p className={`${gridTitle} text-sm`}>
                  Compare seasons: trainability trends, medical injuries history, and availability percentages.
                </p>

                <Divider />

                <ul className={`${gridTitle} mb-4 space-y-1 text-sm`}>
                  <li className="flex items-center gap-2">
                    <BarChart3 size={14} /> Trainability trends (per season window)
                  </li>
                  <li className="flex items-center gap-2">
                    <ShieldCheck size={14} /> Injuries breakdown (type/region)
                  </li>
                  <li className="flex items-center gap-2">
                    <Users2 size={14} /> Availability & absent player-days
                  </li>
                </ul>

                <div className="flex items-center justify-between">
                  <div className="flex flex-wrap items-center gap-2">
                    <TagComp tone="violet">Printable</TagComp>
                  </div>

                  <span
                    className={`inline-flex items-center text-sm font-medium ${
                      isDarkMode ? "text-indigo-300" : "text-indigo-600"
                    }`}
                  >
                    Open report{" "}
                    <ArrowRight className="ml-1 transition-transform group-hover:translate-x-0.5" size={16} />
                  </span>
                </div>
              </div>
            </div>
              </Link>
                </>
              )}
            </>
          )}

          {/* Placeholder / Coming soon
          <div className={`${cardBase} ${cardTone} opacity-60`}>
            <div className="relative flex items-start gap-4">
              <span className={iconWrap(isDarkMode ? "bg-gray-700" : "bg-gray-100", isDarkMode ? "text-gray-300" : "text-gray-500")}>
                <FileBarChart />
              </span>
              <div className="flex-1">
                <div className="mb-1 flex items-center gap-2">
                  <h3 className="text-lg font-semibold">Other report (soon)</h3>
                  <TagComp tone="neutral">Roadmap</TagComp>
                </div>
                <p className={`${gridTitle} text-sm`}>
                  More templates and comparative dashboards are on the way.
                </p>
                <Divider />
                <ul className={`${gridTitle} space-y-1 text-sm`}>
                  <li className="flex items-center gap-2">
                    <BarChart3 size={14} /> Custom KPIs
                  </li>
                  <li className="flex items-center gap-2">
                    <Activity size={14} /> Scenario comparisons
                  </li>
                  <li className="flex items-center gap-2">
                    <ShieldCheck size={14} /> Export presets
                  </li>
                </ul>
              </div>
            </div>
          </div> */}
        </div>

        {/* Micro-help */}
        <p className={`${gridTitle} mt-6 text-xs`}>
          {t("pages.reports.tip")}
        </p>
      </div>
    </div>
  );
}
